import { useEffect, useState } from 'react';

import { getDaysOff } from '../settings/api';
import type { DayOff } from '../settings/models';
import { fetchAvatarObjectUrl, fetchCurrentUser, fetchRoleBudget } from './api';
import type { Caller } from './models';

export type CurrentUserState = {
    status: 'loading' | 'ready' | 'error';
    user: Caller | null;
    // keyed by ISO date
    daysOff: Map<string, DayOff>;
    error: string | null;
};

const initial: CurrentUserState = {
    status: 'loading',
    user: null,
    daysOff: new Map<string, DayOff>(),
    error: null,
};

export const useCurrentUserState = (configured: boolean, org: string, pat: string): CurrentUserState => {
    const [state, setState] = useState<CurrentUserState>(initial);

    useEffect(() => {
        if (!configured) {
            setState(initial);
            return;
        }

        let cancelled = false;
        let mintedAvatarUrl: string | null = null;
        setState({ ...initial, status: 'loading' });

        (async () => {
            try {
                const member = await fetchCurrentUser();
                if (cancelled) {
                    return;
                }

                const [budget, daysOff] = await Promise.all([fetchRoleBudget(member.department), getDaysOff(member.id)]);
                if (cancelled) {
                    return;
                }

                setState({
                    status: 'ready',
                    user: {
                        ...member,
                        avatarObjectUrl: null,
                        targetMinRatio: budget.billableMin,
                        targetMaxRatio: budget.billableMax,
                    },
                    daysOff: new Map(daysOff.items.map((d) => [d.date, d])),
                    error: null,
                });

                const avatarUrl = await fetchAvatarObjectUrl(org, pat);
                if (cancelled) {
                    if (avatarUrl) {
                        URL.revokeObjectURL(avatarUrl);
                    }
                    return;
                }
                if (avatarUrl) {
                    mintedAvatarUrl = avatarUrl;
                    setState((prev) => (prev.user ? { ...prev, user: { ...prev.user, avatarObjectUrl: avatarUrl } } : prev));
                }
            } catch (e) {
                if (cancelled) {
                    return;
                }
                setState({
                    status: 'error',
                    user: null,
                    daysOff: new Map<string, DayOff>(),
                    error: e instanceof Error ? e.message : 'Failed to load caller.',
                });
            }
        })();

        return () => {
            cancelled = true;
            if (mintedAvatarUrl) {
                URL.revokeObjectURL(mintedAvatarUrl);
            }
        };
    }, [configured, org, pat]);

    return state;
};
